import React, { useState, useEffect } from 'react';
import NavigationBar from './NavigationBar';
import HamburgerMenu from './HamburgerMenu';
import { getTopSolvers, getCurrentDate } from './supabaseClient';
import Footer from './Footer';

function LandingPage({ navigateTo, isLoggedIn, userProfile }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [topSolvers, setTopSolvers] = useState([]);
  const [isLoadingLeaderboard, setIsLoadingLeaderboard] = useState(true);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch today's top 5 solvers for Hard difficulty
  useEffect(() => {
    fetchTopSolvers();
  }, []);

  const fetchTopSolvers = async () => {
    setIsLoadingLeaderboard(true);
    try {
      const today = getCurrentDate();
      const solvers = await getTopSolvers('hard', today, 5);
      setTopSolvers(solvers);
    } catch (error) {
      console.error('Error fetching top solvers:', error);
      setTopSolvers([]);
    } finally {
      setIsLoadingLeaderboard(false);
    }
  };

  const handleStartPuzzle = () => {
    navigateTo('difficulty');
  };

  const handleViewLeaderboard = () => {
    navigateTo('leaderboard');
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {!isMobile && <NavigationBar currentPage="landing" isLoggedIn={isLoggedIn} navigateTo={navigateTo} userProfile={userProfile} />}
      {isMobile && <HamburgerMenu currentPage="landing" isLoggedIn={isLoggedIn} navigateTo={navigateTo} userProfile={userProfile} />}

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: '1',
        padding: isMobile ? '70px 16px 20px' : '20px',
        gap: isMobile ? '20px' : '40px'
      }}>
        {/* Brand Section */}
        <div style={{
          textAlign: 'center',
          maxWidth: '600px'
        }}>
          <div style={{
            width: isMobile ? '75px' : '115px',
            height: isMobile ? '75px' : '115px',
            background: 'white',
            borderRadius: isMobile ? '12px' : '18px',
            margin: '0 auto 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            padding: isMobile ? '5px' : '5px'
          }}>
            <img 
              src="/logo.png" 
              alt="SudokuMatch Logo" 
              style={{ 
                width: '100%', 
                height: '100%',
                objectFit: 'contain'
              }}
            />
          </div>
          
          <h1 style={{
            color: 'white',
            fontSize: isMobile ? '26px' : '42px',
            fontWeight: '800',
            margin: '0 0 8px 0',
            letterSpacing: '-0.5px'
          }}>
            SudokuMatch
          </h1>
          
          <p style={{
            color: 'white',
            fontSize: isMobile ? '17px' : '22px',
            margin: '0 0 8px 0',
            fontWeight: '600',
            letterSpacing: '-0.3px'
          }}>
            Find Out How Good You Are
          </p>

          <p style={{
            color: 'rgba(255, 255, 255, 0.85)',
            fontSize: isMobile ? '13px' : '16px',
            margin: '0',
            fontWeight: '500',
            lineHeight: '1.5',
            padding: '0 20px'
          }}>
            Daily Sudoku puzzles. Solve at your pace, or see how your time compares.
          </p>
        </div>

        {/* Action Section */}
        <button
          onClick={handleStartPuzzle}
          style={{
            background: '#FF8C00',
            color: 'white',
            border: 'none',
            padding: isMobile ? '12px 32px' : '16px 48px',
            fontSize: isMobile ? '15px' : '18px',
            fontWeight: '700',
            borderRadius: '10px',
            cursor: 'pointer',
            boxShadow: '0 8px 30px rgba(255, 140, 0, 0.4)',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 12px 40px rgba(255, 140, 0, 0.5)';
            e.target.style.background = '#E07B00';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 8px 30px rgba(255, 140, 0, 0.4)';
            e.target.style.background = '#FF8C00';
          }}
        >
          Solve Today's Puzzle
        </button>

        {/* Proof Section - Leaderboard Preview */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: isMobile ? '20px' : '28px 32px',
          width: isMobile ? 'calc(100% - 32px)' : 'auto',
          minWidth: isMobile ? 'auto' : '440px',
          maxWidth: isMobile ? '100%' : '520px',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <h3 style={{
            color: 'white',
            fontSize: isMobile ? '16px' : '20px',
            fontWeight: '700',
            margin: '0 0 4px 0',
            textAlign: 'center'
          }}>
            Today's Top Solvers — Hard Puzzle
          </h3>
          
          <p style={{
            color: 'rgba(255, 255, 255, 0.75)',
            fontSize: '13px',
            margin: '0 0 16px 0',
            textAlign: 'center',
            fontWeight: '500'
          }}>
            {topSolvers.length > 0 ? 'Live rankings from today' : 'Play to be the first solver today'}
          </p>
          
          <div style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            {isLoadingLeaderboard ? (
              <div style={{
                textAlign: 'center',
                padding: '40px 0',
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '14px'
              }}>
                Loading today's rankings...
              </div>
            ) : topSolvers.length > 0 ? (
              topSolvers.map((player, index) => (
                <div
                  key={player.userId || index}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: isMobile ? '8px 0' : '12px 0',
                    borderBottom: index === topSolvers.length - 1 ? 'none' : '1px solid rgba(255, 255, 255, 0.1)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '10px' : '12px' }}>
                    <span style={{
                      width: isMobile ? '26px' : '28px',
                      height: isMobile ? '26px' : '28px',
                      background: player.rank <= 3 ? '#FF8C00' : 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: isMobile ? '13px' : '14px',
                      fontWeight: '700'
                    }}>
                      {player.rank}
                    </span>
                    <span style={{ 
                      fontWeight: '500', 
                      fontSize: isMobile ? '15px' : '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      {player.username}
                      {player.founderTier === 'paid' && (
                        <span style={{ fontSize: '14px' }}>👑</span>
                      )}
                      {player.founderTier === 'free' && (
                        <span style={{ fontSize: '14px' }}>⭐</span>
                      )}
                    </span>
                  </div>
                  <span style={{
                    fontWeight: '700',
                    fontSize: isMobile ? '15px' : '16px',
                    fontFamily: 'monospace'
                  }}>
                    {player.time}
                  </span>
                </div>
              ))
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '40px 0',
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '14px'
              }}>
                No solves yet today. Be the first! 🎯
              </div>
            )}
            
            {/* Show "early" message when there are 1-4 solvers */}
            {topSolvers.length > 0 && topSolvers.length < 5 && (
              <div style={{
                textAlign: 'center',
                padding: '16px 0 0 0',
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: '13px',
                fontStyle: 'italic'
              }}>
                You're early today — more solvers will appear soon
              </div>
            )}
          </div>

          <div style={{
            textAlign: 'center',
            marginTop: isMobile ? '14px' : '20px',
            paddingTop: isMobile ? '12px' : '16px',
            borderTop: '1px solid rgba(255, 255, 255, 0.15)'
          }}>
            <button
              onClick={handleViewLeaderboard}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                padding: '4px',
                textDecoration: 'none',
                transition: 'opacity 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.opacity = '0.8';
              }}
              onMouseLeave={(e) => {
                e.target.style.opacity = '1';
              }}
            >
              View full leaderboards →
            </button>
          </div>
        </div>
      </div>
      <Footer navigateTo={navigateTo} />
    </div>
  );
}

export default LandingPage;
